import { useMemo } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useExcIceJobV1TaskQueryServiceTaskGet } from '@interxfi/api-client/generated/hooks/exc-ice-job-v1-task-query-service-task-get';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyListStack } from '@interxfi/app-components/components/readonly-list/app-readonly-list-stack';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import type { ReadonlyListGroup } from '@interxfi/design-system/components/list/readonly/stacked/readonly-list-stack';
import { Typography } from '@interxfi/design-system/components/typography';
import type { ExcIceJobV1TaskSpec, ExcIceJobV1TaskState } from '@interxfi/types';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { IceTaskEntityMenu } from '../../../../../../../../../../features/vendor/ice/task/ice-task-entity-menu';
import { IceTaskEventSummary } from '../../../../../../../../../../features/vendor/ice/task/ice-task-event';
import { type getIceTaskSpecListItems, getIceTaskStateListItems } from '../../../../../../../../../../features/vendor/ice/task/ice-task-list-items';
import { getIceTaskDetailPath } from '../../../../../../../../../../features/vendor/ice/task/ice-task-routing';
import { IceTaskStatusPill } from '../../../../../../../../../../features/vendor/ice/task/ice-task-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/ice/task/_task/$taskId')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `ice-task-${params.taskId}`,
        label: 'Task',
        to: getIceTaskDetailPath(params.taskId),
        suffix: <CopyButton content={params.taskId} label="Copy Task ID" tooltip={{ copy: params.taskId, copied: 'Copied Task ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('ICE Task'),
  component: IceTaskDetail,
});

function getSpecTypeListItems(data: ExcIceJobV1TaskSpec | undefined, items: ReturnType<typeof getIceTaskSpecListItems>): ReadonlyListGroup[] {
  const base = [{ id: 'main', items: [items.typeName] }] as const satisfies ReadonlyListGroup[];

  if (!data) {
    return base;
  }

  if ('instrument' in data) {
    if ('instrument' in items) {
      return [
        { id: 'main', items: [items.typeName, items.instrument.value.instrumentId, items.instrument.value.timestamp] },
        {
          id: 'xmlLocation',
          title: 'XML Location',
          items: [
            items.instrument.value.xmlLocation.bucket,
            items.instrument.value.xmlLocation.key,
            items.instrument.value.xmlLocation.length,
            items.instrument.value.xmlLocation.offset,
          ],
        },
      ];
    }

    return base;
  }

  if ('corpAction' in data) {
    if ('corpAction' in items) {
      return [
        { id: 'main', items: [items.typeName, items.corpAction.value.eventId, items.corpAction.value.timestamp] },
        {
          id: 'xmlLocation',
          title: 'XML Location',
          items: [
            items.corpAction.value.xmlLocation.bucket,
            items.corpAction.value.xmlLocation.key,
            items.corpAction.value.xmlLocation.length,
            items.corpAction.value.xmlLocation.offset,
          ],
        },
      ];
    }

    return base;
  }

  exhaustiveCheck(data['!type']);

  return base;
}

function getListItems(data: ExcIceJobV1TaskState | undefined) {
  const items = getIceTaskStateListItems(data);

  return {
    left: getSpecTypeListItems(data?.data?.spec, items.data.spec),
    right: [items.jobId, items.metadata.createdAt, items.metadata.updatedAt, items.taskId] as const satisfies ReadonlyListItem[],
  } as const;
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load ICE task', 'view this ICE task');

function IceTaskDetail() {
  const { taskId } = Route.useParams();
  const request = { taskId } as const;
  const { data, error, isPending } = useExcIceJobV1TaskQueryServiceTaskGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  const listItems = useMemo(() => getListItems(data?.task), [data?.task]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <IceTaskStatusPill isPending={showPending} size="large" status={data?.task?.status} />
          </HorizontalStack>

          <IceTaskEntityMenu {...request} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <IceTaskEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyListStack groups={listItems.left} isPending={showPending} titleGap={4} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>
    </PageLayout>
  );
}
